/**
 * TaskFormModalV5 — Create / Edit Task modal that uses the V5 task card
 * itself as the form surface. The Dialog hosts a permanently-expanded
 * V3TaskCard (forceOpen) so the modal is 100 % design-parity with the
 * cards rendered everywhere else. A single Save / Cancel footer overlays
 * the bottom edge.
 *
 *   <TaskFormModalV5
 *      open={...} onOpenChange={...}
 *      mode="create" | "edit"
 *      task={existing}                 // edit mode
 *      defaultValues={{...}}           // create mode
 *      workAreas={...} personalSubAreas={...} people={...}
 *      onSave={(payload) => ...}
 *      onDelete={(taskId) => ...}      // edit mode only
 *   />
 *
 * Design parity is enforced by REUSING the V5 V3TaskCard rendering engine
 * (same priority gradient header, work-area swatch, P/E pills pulled from
 * v3Shared, status pill, recurring/meeting/people sections). No bespoke
 * field markup lives in this file — only the dialog shell + the
 * draft-collection bridge.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { Save, Trash2, X } from "lucide-react";
import { V3TaskCard, V3_WORK_AREAS, V3_PERSONAL_SUB_AREAS } from "../design-system/OrkaTaskCardV5";

function buildInitial(task, defaultValues, workAreas) {
  const t = task || {};
  const dv = defaultValues || {};
  return {
    task_id:               t.task_id || t.id || undefined,
    task_title:            t.task_title ?? dv.task_title ?? "",
    task_description:      t.task_description ?? dv.task_description ?? "",
    work_area_id:          t.work_area_id || dv.work_area_id || workAreas[0]?.work_area_id || "",
    personal_sub_area_id:  t.personal_sub_area_id || dv.personal_sub_area_id || "",
    task_priority:         t.task_priority ?? dv.task_priority ?? 3,
    task_estimated_effort: t.task_estimated_effort ?? dv.task_estimated_effort ?? 3,
    task_status:           t.task_status || "To Do",
    task_status_key:       t.task_status_key || dv.task_status_key || "todo",
    responsible_person_id: t.responsible_person_id || dv.responsible_person_id || "",
    observer_person_ids:   t.observer_person_ids || dv.observer_person_ids || [],
    task_delivery_date:    t.task_delivery_date || t.task_local_date || dv.task_delivery_date || "",
    task_start_time:       t.task_start_time || t.task_local_start_time || dv.task_start_time || "",
    task_end_time:         t.task_end_time || t.task_local_end_time || dv.task_end_time || "",
    task_planning_date:    t.task_planning_date || dv.task_planning_date || "",
    task_tags:             t.task_tags || dv.task_tags || [],
    is_recurring:          !!(t.is_recurring ?? dv.is_recurring ?? false),
    recurring_interval:    t.recurring_interval || dv.recurring_interval || "weekly",
    recurring_weekdays:    t.recurring_weekdays || dv.recurring_weekdays || [],
    is_meeting:            !!(t.is_meeting ?? dv.is_meeting ?? false),
    meet_link:             t.meet_link || dv.meet_link || "",
    meet_location:         t.meet_location || dv.meet_location || "",
    meet_attendees:        t.meet_attendees || dv.meet_attendees || [],
  };
}

export function TaskFormModalV5({
  open,
  onOpenChange,
  mode = "create",
  task = null,
  defaultValues = {},
  workAreas: providedWorkAreas,
  personalSubAreas: providedSubAreas,
  people,
  onSave,
  onDelete,
}) {
  const workAreas = (providedWorkAreas && providedWorkAreas.length > 0)
    ? providedWorkAreas
    : V3_WORK_AREAS;
  const personalSubAreas = providedSubAreas || V3_PERSONAL_SUB_AREAS;
  // IMPORTANT: derive `initial` only when the dialog OPENS or when the
  // identity of the task being edited changes. Re-running buildInitial on
  // every render created a feedback loop with the setDraft useEffect
  // (iter168 — "Maximum update depth exceeded" on /dashboard-2).
  const taskKey = task ? (task.task_id || task.id || 'new') : 'new';
  const [draft, setDraft] = useState(() => buildInitial(task, defaultValues, workAreas));
  useEffect(() => {
    if (open) setDraft(buildInitial(task, defaultValues, workAreas));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskKey]);

  const handleCardChange = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const handleSave = () => {
    if (!draft.task_title?.trim()) return;
    onSave?.(draft);
    onOpenChange?.(false);
  };

  const handleDelete = () => {
    if (!draft.task_id) return;
    if (!window.confirm("Delete this task? This action cannot be undone.")) return;
    onDelete?.(draft.task_id);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden bg-transparent border-0 shadow-none"
        data-testid="task-form-modal-v5"
      >
        {/* The V3TaskCard renders ITSELF with the priority gradient + scope.
            We give it extra bottom padding via wrapper so the floating
            footer never overlaps actual content. */}
        <div className="relative max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl">
          <div className="overflow-y-auto max-h-[90vh] pb-20 bg-white dark:bg-slate-900">
            {/* The V3TaskCard CSS is scoped to .orka-v3-scope; without
                that wrapper the SVG icons + layout collapse to defaults. */}
            <div className="orka-v3-scope" style={{ width: '100%' }}>
              <V3TaskCard
                task={draft}
                forceOpen
                workAreas={workAreas}
                personalSubAreas={personalSubAreas}
                people={people}
                onChange={handleCardChange}
                hideGrip
              />
            </div>
          </div>
          {/* Floating Save/Cancel footer */}
          <div
            className="absolute left-0 right-0 bottom-0 px-5 py-3 flex items-center gap-2 backdrop-blur-md border-t"
            style={{ background: 'rgba(255,255,255,.92)', borderColor: 'rgba(15,23,42,.08)' }}
            data-testid="task-form-modal-v5-footer"
          >
            {mode === "edit" && draft.task_id && (
              <button
                type="button"
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                data-testid="task-form-modal-v5-delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-bold text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
              data-testid="task-form-modal-v5-cancel"
            >
              <X className="w-3.5 h-3.5" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!draft.task_title?.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 text-white text-xs font-bold shadow-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="task-form-modal-v5-save"
            >
              <Save className="w-3.5 h-3.5" />
              {mode === "edit" ? "Save changes" : "Create task"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CreateTaskModalV5(props) {
  return <TaskFormModalV5 {...props} mode="create" />;
}

export function EditTaskModalV5(props) {
  return <TaskFormModalV5 {...props} mode="edit" />;
}

export default TaskFormModalV5;
